import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { OfflineProvider } from "@/contexts/OfflineContext";
import OfflineStatusBar from "@/components/OfflineStatusBar";

// Re-export axios utilities from lib for backward compatibility
export { axiosInstance, API } from "@/lib/axios";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import CompanyRegister from "@/pages/CompanyRegister";
import WorkerDashboard from "@/pages/WorkerDashboard";
import WorkerHome from "@/pages/WorkerHome";
import WorkerTraining from "@/pages/WorkerTraining";
import WorkerNearMiss from "@/pages/WorkerNearMiss";
import WorkerMessages from "@/pages/WorkerMessages";
import WorkerOnboarding from "@/pages/WorkerOnboarding";
import WorkerJSAs from "@/pages/WorkerJSAs";
import WorkerProjectJHAs from "@/pages/WorkerProjectJHAs";
import WorkerCertificates from "@/pages/WorkerCertificates";
import WorkerToolboxTalks from "@/pages/WorkerToolboxTalks";
import WorkerInspections from "@/pages/WorkerInspections";
import WorkerProfile from "@/pages/WorkerProfile";
import SupervisorDashboard from "@/pages/SupervisorDashboard";
import SupervisorOverview from "@/pages/SupervisorOverview";
import SupervisorHome from "@/pages/SupervisorHome";
import SupervisorJSAs from "@/pages/SupervisorJSAs";
import SupervisorIncidents from "@/pages/SupervisorIncidents";
import FieldLogs from "@/pages/FieldLogs";
import SuperintendentDashboard from "@/pages/SuperintendentDashboard";
import JSAManager from "@/components/JSAManager";
import SupervisorAudits from "@/pages/SupervisorAudits";
import SupervisorToolbox from "@/pages/SupervisorToolbox";
import SupervisorTeam from "@/pages/SupervisorTeam";
import SupervisorInspections from "@/pages/SupervisorInspections";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminHome from "@/pages/AdminHome";
import AdminNearMiss from "@/pages/AdminNearMiss";
import AdminIncidents from "@/pages/AdminIncidents";
import AdminJSAs from "@/pages/AdminJSAs";
import AdminAudits from "@/pages/AdminAudits";import AdminUsers from "@/pages/AdminUsers";
import AdminPolicyViolations from "@/pages/AdminPolicyViolations";
import AdminOSHAReports from "@/pages/AdminOSHAReports";
import AdminLOTOPoints from "@/pages/AdminLOTOPoints";
import AdminPendingApprovals from "@/pages/AdminPendingApprovals";
import PendingApprovalBanner from "@/components/PendingApprovalBanner";
import AdminTileSettings from "@/pages/AdminTileSettings";
import AdminTraining from "@/pages/AdminTraining";
import AdminNewHires from "@/pages/AdminNewHires";
import AdminInspectionTemplates from "@/pages/AdminInspectionTemplates";
import SDSLookup from "@/pages/SDSLookup";
import AdminToolbox from "@/pages/AdminToolbox";
import AdminForms from "@/pages/AdminForms";
import AdminSettings from "@/pages/AdminSettings";
import AdminMessages from "@/pages/AdminMessages";
import AdminPolicies from "@/pages/AdminPolicies";
import AdminCustomForms from "@/pages/AdminCustomForms";
import AdminFormMapper from "@/pages/AdminFormMapper";
import AdminPdfCreator from "@/pages/AdminPdfCreator";
import WorkerCustomForms from "@/pages/WorkerCustomForms";
import WorkerFillForm from "@/pages/WorkerFillForm";
import SuperAdminDashboard from "@/pages/SuperAdminDashboard";
import AiModelControls from "@/pages/AiModelControls";
import SuperAdminHistoricalReview from "@/pages/SuperAdminHistoricalReview";
import OpenAIByoKeySetup from "@/pages/OpenAIByoKeySetup";
import ModuleAccess from "@/pages/ModuleAccess";
import CompanyAuditDashboard from "@/pages/CompanyAuditDashboard";
import JSAUpdateQueue from "@/pages/JSAUpdateQueue";
import AnalyticsMockup from "@/pages/AnalyticsMockup";
import SafetyAnalyticsMockup from "@/pages/SafetyAnalyticsMockup";
import SafetyDashboard from "@/pages/SafetyDashboard";
import CourseDetail from "@/pages/CourseDetail";
import Certificates from "@/pages/Certificates";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import CraftManagement from "@/pages/CraftManagement";
import VerifyWorker from "@/pages/VerifyWorker";
import Divisions from "@/pages/Divisions";
import DivisionAnalytics from "@/pages/DivisionAnalytics";
import SafetyAssistant from "@/pages/SafetyAssistant";
import FileManager from "@/pages/FileManager";
import SubcontractorManagement from "@/pages/SubcontractorManagement";
import GCDashboard from "@/pages/GCDashboard";
import GCSubcontractorAnalytics from "@/pages/GCSubcontractorAnalytics";
import GCAnalyticsDashboard from "@/pages/GCAnalyticsDashboard";
import SubcontractorProjectsView from "@/pages/SubcontractorProjectsView";
import GCCorrectiveActions from "@/pages/GCCorrectiveActions";
import SubCorrectiveActions from "@/pages/SubCorrectiveActions";
import MyCorrectiveActions from "@/pages/MyCorrectiveActions";
import GCProjectDetail from "@/pages/GCProjectDetail";
import GCProjectDashboard from "@/pages/GCProjectDashboard";
import ParentGCDashboard from "@/pages/ParentGCDashboard";
import PreConstructionJHA from "@/pages/PreConstructionJHA";
import InstallPWA from "@/components/InstallPWA";
import PushPermissionPrompt from "@/components/PushPermissionPrompt";

function AppRoutes() {
  const { user, loading, logout, login } = useAuth();
  
  // Create a setUser function that works with the auth context
  const setUser = (userData) => {
    if (userData) {
      // If setting user data, we need a token (this is mainly for compatibility)
      const token = localStorage.getItem('token');
      if (token) {
        login(userData, token);
      }
    } else {
      // If clearing user data, logout
      logout();
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
  };

  // Helper function to check if user has admin access
  const isAdmin = (user) => {
    return user && (user.role === 'company_admin' || user.role === 'admin' || user.role === 'division_admin' || user.role === 'gc_admin');
  };

  // Check if user role supports offline mode
  const supportsOffline = (user) => {
    // Include all roles that can access JSA functionality (which uses useOffline hook)
    return user && (user.role === 'worker' || user.role === 'service_worker' || user.role === 'supervisor' || user.role === 'company_admin' || user.role === 'division_admin');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-xl font-medium text-slate-600">Loading...</div>
      </div>
    );
  }

  // Wrap content with OfflineProvider for workers and supervisors
  const content = (
    <>
      <InstallPWA />
      {user && <PushPermissionPrompt user={user} />}
      {supportsOffline(user) && <OfflineStatusBar />}
      {user && <PendingApprovalBanner />}
      <Routes>
        <Route 
          path="/" 
          element={!user ? <Login /> : 
            user.role === 'worker' || user.role === 'service_worker' ? <Navigate to="/dashboard" replace /> :
            user.role === 'supervisor' ? <Navigate to="/supervisor" replace /> :
            user.role === 'superintendent' ? <Navigate to="/superintendent" replace /> :
            (user.role === 'gc_admin' && user.isParentGc) ? <Navigate to="/parent-gc/dashboard" replace /> :
            user.role === 'gc_admin' ? <Navigate to="/gc/dashboard" replace /> :
            user.role === 'company_admin' || user.role === 'division_admin' ? <Navigate to="/admin" replace /> :
            user.role === 'super_admin' ? <Navigate to="/superadmin" replace /> :
            <Navigate to="/admin" replace />}
        />
        <Route 
          path="/login" 
          element={!user ? <Login /> : <Navigate to="/" replace />}
        />
        <Route 
          path="/register" 
          element={!user ? <Register /> : <Navigate to="/" replace />}
        />
        <Route 
          path="/company-register" 
          element={!user ? <CompanyRegister /> : <Navigate to="/" replace />}
        />
        <Route 
          path="/forgot-password" 
          element={!user ? <ForgotPassword /> : <Navigate to="/" replace />}
        />
        {/* Public pages - no auth required */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route 
          path="/superadmin" 
          element={user && user.role === 'super_admin' ? <SuperAdminDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />}
        />
        <Route 
          path="/superadmin/modules" 
          element={user && user.role === 'super_admin' ? <ModuleAccess /> : <Navigate to="/" replace />}
        />
        <Route 
          path="/superadmin/openai-byo-key" 
          element={user && user.role === 'super_admin' ? <OpenAIByoKeySetup /> : <Navigate to="/" replace />}
        />
        <Route 
          path="/superadmin/ai-models" 
          element={user && user.role === 'super_admin' ? <AiModelControls /> : <Navigate to="/" replace />}
        />
        <Route 
          path="/superadmin/historical-review" 
          element={user && user.role === 'super_admin' ? <SuperAdminHistoricalReview /> : <Navigate to="/" replace />}
        />
        <Route 
          path="/dashboard" 
          element={user && (user.role === 'worker' || user.role === 'service_worker') ? 
            <WorkerHome user={user} onLogout={handleLogout} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/worker/training" 
          element={user && (user.role === 'worker' || user.role === 'service_worker') ? 
            <WorkerTraining user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/worker/project-jhas" 
          element={user && (user.role === 'worker' || user.role === 'service_worker') ? 
            <WorkerProjectJHAs user={user} onLogout={handleLogout} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/worker/near-miss" 
          element={user && (user.role === 'worker' || user.role === 'service_worker') ? 
            <WorkerNearMiss user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/worker/sds-lookup" 
          element={user && (user.role === 'worker' || user.role === 'service_worker') ? 
            <SDSLookup userRole="worker" /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/worker/messages" 
          element={user && (user.role === 'worker' || user.role === 'service_worker') ? 
            <WorkerMessages user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/worker/onboarding" 
          element={user && (user.role === 'worker' || user.role === 'service_worker') ? 
            <WorkerOnboarding user={user} onLogout={handleLogout} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/worker/jsas" 
          element={user && (user.role === 'worker' || user.role === 'service_worker') ? 
            (user.role === 'service_worker' ? <JSAManager user={user} /> : <WorkerJSAs user={user} />) : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/worker/certificates" 
          element={user && (user.role === 'worker' || user.role === 'service_worker') ? 
            <WorkerCertificates user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/worker/toolbox-talks" 
          element={user && (user.role === 'worker' || user.role === 'service_worker') ? 
            (user.role === 'service_worker' ? <SupervisorToolbox user={user} setUser={setUser} /> : <WorkerToolboxTalks user={user} />) : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/worker/inspections" 
          element={user && (user.role === 'worker' || user.role === 'service_worker') ? 
            <WorkerInspections user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/worker/forms" 
          element={user && (user.role === 'worker' || user.role === 'service_worker') ? 
            <WorkerCustomForms user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/worker/forms/:formId/fill" 
          element={user && (user.role === 'worker' || user.role === 'service_worker') ? 
            <WorkerFillForm user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/worker/profile" 
          element={user && (user.role === 'worker' || user.role === 'service_worker') ? 
            <WorkerProfile user={user} onLogout={handleLogout} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/supervisor" 
          element={user && user.role === 'supervisor' ? 
            <SupervisorHome user={user} onLogout={handleLogout} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/supervisor/overview" 
          element={user && user.role === 'supervisor' ? 
            <SupervisorOverview user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/supervisor/jsas" 
          element={user && user.role === 'supervisor' ? 
            <SupervisorJSAs user={user} setUser={setUser} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/supervisor/incidents" 
          element={user && user.role === 'supervisor' ? 
            <SupervisorIncidents user={user} setUser={setUser} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/supervisor/audits" 
          element={user && user.role === 'supervisor' ? 
            <SupervisorAudits user={user} setUser={setUser} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/supervisor/inspections" 
          element={user && user.role === 'supervisor' ? 
            <SupervisorInspections user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/supervisor/field-logs" 
          element={user && (user.role === 'supervisor' || user.role === 'admin') ? 
            <FieldLogs user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/superintendent" 
          element={user && user.role === 'superintendent' ? 
            <SuperintendentDashboard user={user} onLogout={handleLogout} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/worker/field-logs" 
          element={user && (user.role === 'worker' || user.role === 'service_worker') ? 
            <FieldLogs user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/field-logs" 
          element={user && (user.role === 'admin' || user.role === 'company_admin' || user.role === 'division_admin') ? 
            <FieldLogs user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/supervisor/toolbox" 
          element={user && user.role === 'supervisor' ? 
            <SupervisorToolbox user={user} setUser={setUser} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/supervisor/sds-lookup" 
          element={user && user.role === 'supervisor' ? 
            <SDSLookup userRole="supervisor" /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/supervisor/team" 
          element={user && user.role === 'supervisor' ? 
            <SupervisorTeam user={user} setUser={setUser} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/supervisor/pre-construction-jha" 
          element={user && user.role === 'supervisor' ? 
            <PreConstructionJHA /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin" 
          element={user && (isAdmin(user) || user.role === 'division_admin') ? 
            <AdminHome user={user} setUser={setUser} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/near-miss" 
          element={user && (isAdmin(user)) ? 
            <AdminNearMiss user={user} setUser={setUser} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/incidents" 
          element={user && (isAdmin(user)) ? 
            <AdminIncidents user={user} setUser={setUser} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/jsas" 
          element={user && (isAdmin(user)) ? 
            <AdminJSAs user={user} setUser={setUser} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/jsa" 
          element={user && (isAdmin(user)) ? 
            <SupervisorJSAs user={user} setUser={setUser} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/audits" 
          element={user && (isAdmin(user)) ? 
            <AdminAudits user={user} setUser={setUser} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/policy-violations" 
          element={user && (isAdmin(user)) ? 
            <AdminPolicyViolations user={user} setUser={setUser} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/osha-reports" 
          element={user && (isAdmin(user)) ? 
            <AdminOSHAReports user={user} onLogout={handleLogout} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/loto-points" 
          element={user && (isAdmin(user)) ? 
            <AdminLOTOPoints /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/pending-approvals" 
          element={user && (isAdmin(user)) ? 
            <AdminPendingApprovals /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/tile-settings" 
          element={user && user.role === 'company_admin' ? 
            <AdminTileSettings user={user} setUser={setUser} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/users" 
          element={user && (isAdmin(user)) ? 
            <AdminUsers user={user} setUser={setUser} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/training" 
          element={user && (isAdmin(user)) ? 
            <AdminTraining user={user} setUser={setUser} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/new-hires" 
          element={user && (isAdmin(user)) ? 
            <AdminNewHires /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/inspection-templates" 
          element={user && (isAdmin(user)) ? 
            <AdminInspectionTemplates user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/inspections" 
          element={user && (isAdmin(user)) ? 
            <SupervisorInspections user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/old-dashboard" 
          element={user && (isAdmin(user)) ? 
            <AdminDashboard user={user} onLogout={handleLogout} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/file-manager" 
          element={user && (isAdmin(user)) ? 
            <FileManager user={user} setUser={setUser} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/toolbox" 
          element={user && (isAdmin(user)) ? 
            <AdminToolbox user={user} setUser={setUser} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/forms" 
          element={user && (isAdmin(user)) ? 
            <AdminForms user={user} setUser={setUser} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/settings" 
          element={user && (user.role === 'company_admin' || user.role === 'admin' || user.role === 'division_admin') ? 
            <AdminSettings user={user} setUser={setUser} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/pre-construction-jha" 
          element={user && (user.role === 'company_admin' || user.role === 'admin' || user.role === 'gc_admin' || user.role === 'super_admin') ? 
            <PreConstructionJHA /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/analytics-preview" 
          element={<AnalyticsMockup />}
        />
        <Route 
          path="/safety-analytics-preview" 
          element={<SafetyAnalyticsMockup user={user} />}
        />
        <Route 
          path="/admin/safety-analytics" 
          element={user && (user.role === 'company_admin' || user.role === 'admin' || user.role === 'super_admin' || user.role === 'division_admin') ? 
            <SafetyAnalyticsMockup user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/safety-dashboard" 
          element={user && (user.role === 'company_admin' || user.role === 'admin' || user.role === 'super_admin' || user.role === 'division_admin') ? 
            <SafetyDashboard /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/custom-forms" 
          element={user && (user.role === 'company_admin' || user.role === 'admin' || user.role === 'super_admin' || user.role === 'division_admin') ? 
            <AdminCustomForms user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/custom-forms/create" 
          element={user && (user.role === 'company_admin' || user.role === 'admin' || user.role === 'super_admin' || user.role === 'division_admin') ? 
            <AdminPdfCreator user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/custom-forms/:formId/map" 
          element={user && (user.role === 'company_admin' || user.role === 'admin' || user.role === 'super_admin' || user.role === 'division_admin') ? 
            <AdminFormMapper user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/divisions" 
          element={user && (user.role === 'company_admin' || user.role === 'admin' || user.role === 'division_admin') ? 
            <Divisions user={user} setUser={setUser} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/divisions/:divisionId/analytics" 
          element={user && (user.role === 'company_admin' || user.role === 'division_admin' || user.role === 'admin') ? 
            <DivisionAnalytics user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/subcontractors" 
          element={user && (user.role === 'company_admin' || user.role === 'admin' || user.role === 'division_admin') ? 
            <SubcontractorManagement user={user} setUser={setUser} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/gc/dashboard" 
          element={user && (user.role === 'company_admin' || user.role === 'admin' || user.role === 'gc_admin') ? 
            (user.isParentGc ? <Navigate to="/parent-gc/dashboard" replace /> : <GCDashboard user={user} setUser={setUser} />) : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/parent-gc/dashboard" 
          element={user && (user.role === 'company_admin' || user.role === 'admin' || user.role === 'gc_admin') ? 
            <ParentGCDashboard user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/gc/projects/:projectId" 
          element={user && (user.role === 'company_admin' || user.role === 'admin' || user.role === 'gc_admin') ? 
            <GCProjectDashboard user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/gc/projects/:projectId/detail" 
          element={user && (user.role === 'company_admin' || user.role === 'admin' || user.role === 'gc_admin') ? 
            <GCProjectDetail user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/gc/projects/:projectId/sub/:subCompanyId" 
          element={user && (user.role === 'company_admin' || user.role === 'admin' || user.role === 'gc_admin') ? 
            <GCSubcontractorAnalytics user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/gc/analytics" 
          element={user && (user.role === 'company_admin' || user.role === 'admin' || user.role === 'gc_admin') ? 
            <GCAnalyticsDashboard user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/gc/analytics/:projectId" 
          element={user && (user.role === 'company_admin' || user.role === 'admin' || user.role === 'gc_admin') ? 
            <GCAnalyticsDashboard user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/sub/my-projects" 
          element={user && (user.role === 'company_admin' || user.role === 'admin' || user.role === 'division_admin') ? 
            <SubcontractorProjectsView user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/gc/corrective-actions" 
          element={user && (user.role === 'company_admin' || user.role === 'admin' || user.role === 'gc_admin') ? 
            <GCCorrectiveActions user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route
          path="/my-corrective-actions"
          element={user ?
            <MyCorrectiveActions user={user} /> :
            <Navigate to="/" replace />}
        />
        <Route 
          path="/sub/corrective-actions" 
          element={user && (user.role === 'company_admin' || user.role === 'admin' || user.role === 'division_admin') ? 
            <SubCorrectiveActions user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/policies" 
          element={user && (isAdmin(user)) ? 
            <AdminPolicies user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/messages" 
          element={user && (isAdmin(user)) ? 
            <AdminMessages user={user} setUser={setUser} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/admin/sds-library" 
          element={user && (user.role === 'company_admin' || user.role === 'super_admin' || user.role === 'division_admin') ? 
            <SDSLookup userRole="admin" /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/company/audits" 
          element={user && (user.role === 'company_admin' || user.role === 'super_admin' || user.role === 'division_admin') ? 
            <CompanyAuditDashboard user={user} onLogout={handleLogout} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/jsa-updates" 
          element={user && (user.role === 'supervisor' || user.role === 'division_admin' || user.role === 'company_admin') ? 
            <JSAUpdateQueue user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/course/:courseId" 
          element={user ? 
            <CourseDetail user={user} onLogout={handleLogout} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/certificates" 
          element={user ? 
            <Certificates user={user} onLogout={handleLogout} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/safety-assistant" 
          element={user ? 
            <SafetyAssistant user={user} /> : 
            <Navigate to="/" replace />}
        />
        <Route 
          path="/craft-management" 
          element={user && (user.role === 'company_admin' || user.role === 'super_admin' || user.role === 'division_admin') ? 
            <CraftManagement /> : 
            <Navigate to="/" replace />}
        />
        {/* Public route - no authentication required */}
        <Route 
          path="/verify/:token" 
          element={<VerifyWorker />}
        />
      </Routes>
    </>
  );

  // Wrap with OfflineProvider only for workers and supervisors
  if (supportsOffline(user)) {
    return (
      <OfflineProvider user={user}>
        {content}
      </OfflineProvider>
    );
  }

  return content;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
